import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { IPlatformVersionChange } from '../../../../models/utils/platformCustomizations';
import { getSocialMediaPlatformLogoUrl } from '../../../../models/utils/socialUtils';
import { PlatformCustomizationToggle } from './PlatformCustomizationToggle';
import { PlatformVersionReset } from './PlatformVersionReset';
import { PlatformVersionTabs } from './PlatformVersionTabs';
import { styleSheet } from './styles';

interface IProps {
	activePlatform: Api.SocialMediaType;
	/** Controls that act on the whole version, shown between the reset and the toggle. */
	actions?: React.ReactNode;
	discardedChanges: IPlatformVersionChange[];
	discardSpreadsMedia: boolean;
	isPlatformEdited: (platform: Api.SocialMediaType) => boolean;
	isSplit: boolean;
	/** The limits pill, parked in the gap the lead and the actions leave between them. */
	limits?: React.ReactNode;
	onResetToBase: () => void;
	onSelectPlatform: (platform: Api.SocialMediaType) => void;
	onToggleSplit?: (customizeByPlatform: boolean) => void;
	/** The body and media the tabs switch together, so the tab list is announced against both. */
	panelId: string;
	platformHasErrors: (platform: Api.SocialMediaType) => boolean;
	platforms: Api.SocialMediaType[];
	readOnly?: boolean;
}

const describeOnePost = (platforms: Api.SocialMediaType[]) =>
	platforms.length > 1 ? `One post for all ${platforms.length} platforms` : `One post for ${platforms[0]}`;

/**
 * The strip above the body and the media. While the post is split it is the tab bar for both:
 * a tab selects the version, and everything under it — wording, media, limits — is that
 * version's. Before the split it shows the single post the tabs would divide.
 */
export const PlatformVersionBar: React.FC<IProps> = ({
	activePlatform,
	actions,
	discardedChanges,
	discardSpreadsMedia,
	isPlatformEdited,
	isSplit,
	limits,
	onResetToBase,
	onSelectPlatform,
	onToggleSplit,
	panelId,
	platformHasErrors,
	platforms,
	readOnly,
}) => {
	// One platform has nothing to split between. A post already split keeps the switch, or dropping to
	// one platform would leave no way back to the base post.
	const canSplit = isSplit || platforms.length > 1;
	return (
		<div className={css(styleSheet.versionBar, isSplit && styleSheet.versionBarSplit)}>
			{isSplit ? (
				<PlatformVersionTabs
					activePlatform={activePlatform}
					controlsId={panelId}
					isPlatformEdited={isPlatformEdited}
					onSelect={onSelectPlatform}
					platformHasErrors={platformHasErrors}
					platforms={platforms}
				/>
			) : platforms.length ? (
				<div className={css(styleSheet.versionLead)}>
					<span className={css(styleSheet.versionLeadLogos)}>
						{platforms.map(platform => (
							<img
								alt=''
								className={css(styleSheet.versionLeadLogo)}
								key={platform}
								src={getSocialMediaPlatformLogoUrl(platform)}
							/>
						))}
					</span>
					<span>{describeOnePost(platforms)}</span>
				</div>
			) : null}
			{limits ? (
				<div className={css(styleSheet.versionBarLimits, isSplit && styleSheet.versionBarSplitSlot)}>{limits}</div>
			) : null}
			<div className={css(styleSheet.versionBarActions, isSplit && styleSheet.versionBarSplitSlot)}>
				{isSplit ? (
					<PlatformVersionReset
						isEdited={isPlatformEdited(activePlatform)}
						onResetToBase={onResetToBase}
						platform={activePlatform}
						readOnly={readOnly}
					/>
				) : null}
				{actions}
				{onToggleSplit && canSplit ? (
					<PlatformCustomizationToggle
						changes={discardedChanges}
						isSplit={isSplit}
						onChange={onToggleSplit}
						spreadsMedia={discardSpreadsMedia}
					/>
				) : null}
			</div>
		</div>
	);
};
